/**
 * Relationship-related strings.
 */
import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Icons from '../ui/IconButton/Icons';

const familial = <b>Familial</b>;
const legal = <b>Legal</b>;

const addIcon = <FontAwesomeIcon icon={Icons.ADD.reactIcon}/>;

export default {
    d_relationship: 'Relationship',
    d_relationships: 'Relationships',
    h_relationships: `<div>
        <p>This section lists the relationships between this contact and other contacts in the system.</p>
        <p>The <b>Contact</b> names the contact that is related to the current contact, and the <b>Relationship</b> gives the relationship of the listed contact to the current contact.</p>
        <p>For example, if we are editing the contact John Doe, we could add an entry for Jane Doe with the relationship Spouse.</p>
        <p>The <b>Description</b> field enables you to add more context, such as "first wife", without having to create a new Relationship type of "First Wife", "Second Wife", etc.</p>
        <p>If the relationship is bi-directional, e.g. husband/wife, then creating a new relationship will prompt for the creation of the opposite relationship on save. For example, if we add "Jane Doe" as a relationship for "John Doe" and specify the relationship "Wife" for Jane, when we save we will be asked if we would like to add John Doe with a relationship of "Husband" to Jane Doe.</p>
        <p>Relationships are a key part of thorough <b>Conflict Checking</b>. By adding relationships, Outlaw Practice is able to accurately portray conflicts when checking <b>Leads</b> and <b>Cases</b>.</p>
    </div>`,

    d_participant: 'Participant',
    d_participants: 'Participants',
    h_participants: <div>
        <p>This section lists participants of this case, and their relationship to the case.</p>
        <p>There are both {familial} and {legal} relationships in the lists of available relationships.</p>
        <p>{familial} relationships include labels like "Mother", "Brother" or "Baby Mama".</p>
        <p>{legal} relationships include labels like "Opposing Party", "Judge" or "Co-Counsel".</p>
        <p>The <b>Checked</b> column indicates if the participant has been conflict checked yet. Once a conflict check
            has been run, the date of the conflict check will be added to this column. Until then, an X will be placed
            in the column to indicate that a conflict check needs to be run.</p>
        <p>The <b>Description</b> field enables you to add more context, such as "first wife", without having to
            create a new Relationship type of "First Wife", "Second Wife", etc.</p>
        <p>If the relationship is {familial} and bi-directional, e.g. husband/wife, then creating a new relationship
            will prompt for the creation of the opposite relationship on save. For example, if we add "Jane Doe" as a
            relationship for "John Doe" and specify the relationship "Wife" for Jane, when we save we will be asked if
            we would like to add John Doe with a relationship of "Husband" to Jane Doe.
        </p>
        <p>Participants are a key part of thorough <b>Conflict Checking</b>. By adding participants, Outlaw Practice
            is able to accurately portray conflicts when checking <b>Leads</b> and <b>Cases</b>.</p>
    </div>,

    d_employees: 'Employees',
    h_employees: <div>
        <p>This section lists the employees for this company that are in your <b>Contacts</b>,
            as well as their contact info and related leads and cases.</p>
        <p>You can add new or existing employees by entering or selecting their name in the dropdown at the
            top of the section, then clicking the {addIcon} Add button after the dropdown.</p>
    </div>,

    d_updateOpposite: (params: {recipName: string}) => <div>Update {params.recipName}?</div>,

    d_addOpposite: (params: {recipName: string, sourceName: string}) =>
        <div>
            <p>Should we add an entry in {params.recipName}'s Relationships for {params.sourceName}?</p>
            <p>Choose {params.sourceName}'s relationship to {params.recipName}.</p>
        </div>
    ,

    d_contact: 'Contact',
    h_contact: `<div>
        <p>Which contact do you want to add a relationship for?</p>
        <p>Note that if a contact has multiple relationships, you can add all of the relationships below, and add a description. Do not add the same contact more than once.</p>
    </div>`,

    d_relationshipsField: 'Relationship(s)',
    h_relationshipsField: (params: {type: string}) => `<div>
        <p>The relationship of this contact to this ${params.type}, e.g. ${params.type === 'case' ? 'Opposing Counsel' : 'Spouse'}.</p>
    </div>`,

    d_description: 'Description',
    h_description: `<div>
        <p>An optional description to add context or disambiguate this relationship.</p>
    </div>`,

    d_checked: 'Checked?',

    d_email: 'Email',

    d_phone: 'Phone'

};